import * as React from 'react';
import { Button, Dropdown, notification } from 'antd';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { setReportShare } from './api';

const ShareControls: React.FC<{ reportId: number; shareToken?: string | null }> = ({ reportId, shareToken }) => {
    const qc = useQueryClient();
    const share = useMutation({
        mutationFn: (enabled: boolean) => setReportShare(reportId, enabled),
        onSuccess: (data) => {
            qc.setQueryData(['social-metrics-report', reportId], (prev: unknown) => {
                if (!prev || typeof prev !== 'object') return prev;
                return { ...prev, share_token: data.share_token };
            });
        },
    });

    const copyLink = (token: string) => {
        const url = `${window.location.origin}/share/social-metrics/${token}`;
        navigator.clipboard?.writeText(url).then(
            () => notification.success({ message: 'Link copied. Anyone with it can view this report.' }),
            () => notification.info({ message: 'Share link', description: url, duration: 0 }),
        );
    };

    const enableShare = async () => {
        try {
            const res = await share.mutateAsync(true);
            if (res.share_token) copyLink(res.share_token);
        } catch (e: unknown) {
            const err = e as { response?: { data?: { error?: string } } };
            notification.error({ message: err?.response?.data?.error || 'Could not share this report' });
        }
    };

    if (shareToken) {
        return (
            <Dropdown
                menu={{
                    items: [
                        { key: 'copy', label: 'Copy link' },
                        { key: 'stop', label: 'Stop sharing' },
                    ],
                    onClick: ({ key }) => {
                        if (key === 'copy') copyLink(shareToken);
                        if (key === 'stop') share.mutate(false);
                    },
                }}
            >
                <Button loading={share.isLoading}>Shared</Button>
            </Dropdown>
        );
    }

    return (
        <Button loading={share.isLoading} onClick={enableShare}>
            Share
        </Button>
    );
};

export default ShareControls;
